/**
 * DATA DECONFIG TASK DETAIL PAGE
 *
 * Data team deconfiguration page for location deactivation.
 * Allows data team to confirm data pipeline cleanup and complete the task.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/status-badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Database, XCircle, CheckCircle, Loader2, ArrowLeft, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { Task, Organization, Location } from '@/types/database';

interface TaskWithRelations extends Task {
  organization: Pick<Organization, 'id' | 'name' | 'status' | 'logo_url'> | null;
  location: Location | null;
}

function DataDeconfigTaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [task, setTask] = useState<TaskWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState<any>(null);
  
  // Data deconfiguration checklist
  const [checklist, setChecklist] = useState({
    stoppedPipelines: false,
    archivedData: false,
    cleanedBigQuery: false,
    documentedRemoval: false,
  });

  // Auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== 'DATA_TEAM' && user.role !== 'DATA_ADMIN' && user.role !== 'SYSTEM_ADMIN')) {
    navigate('/unauthorized');
    return null;
  }

  // Data fetching
  useEffect(() => {
    const fetchTask = async () => {
      if (!taskId) {
        navigate('/data-tasks');
        return;
      }

      try {
        setLoading(true);
        const supabase = createClient();

        // Fetch task with organization and location details
        const { data: taskData, error: taskError } = await supabase
          .from('tasks')
          .select(`
            *,
            organization:organizations(id, name, status, logo_url),
            location:locations(
              id,
              name,
              description,
              logo_url,
              source_schema,
              open_dental_source_schema,
              open_dental_clinic,
              source_name,
              status,
              mapping_status
            )
          `)
          .eq('id', taskId)
          .eq('type', 'DATA_DECONFIG')
          .single();

        if (taskError) {
          console.error('Error fetching task:', taskError);
          navigate('/data-tasks');
          return;
        }

        if (!taskData) {
          navigate('/data-tasks');
          return;
        }

        setTask(taskData as TaskWithRelations);

        // Fetch onboarding_queue data separately using organization ID
        if (taskData.organization_id) {
          const { data: queueData } = await supabase
            .from('onboarding_queue')
            .select('primary_contact_name, primary_contact_email, phone_number')
            .eq('id', taskData.organization_id)
            .maybeSingle();

          if (queueData) {
            setContactInfo(queueData);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [taskId, navigate]);

  const allChecklistComplete = Object.values(checklist).every(Boolean);

  const handleComplete = async () => {
    if (!task || !allChecklistComplete) return;

    try {
      setCompleting(true);
      setError(null);
      const supabase = createClient();

      // Mark task as complete
      const { error: taskError } = await supabase
        .from('tasks')
        .update({
          status: 'COMPLETED',
          completed_at: new Date().toISOString(),
          completed_by: user.id,
          metadata: {
            ...task.metadata,
            checklist,
            completed_steps: Object.keys(checklist).filter(key => checklist[key as keyof typeof checklist]),
          },
        })
        .eq('id', task.id);

      if (taskError) throw taskError;

      // Check if this is a deletion queue task
      const deletionQueueId = task.metadata?.deletion_queue_id;
      
      if (deletionQueueId && task.location_id) {
        // This is a deletion task - fetch the deletion queue to check stage
        const { data: deletionQueue } = await supabase
          .from('location_onboarding_deletion_queue')
          .select('deletion_stage')
          .eq('id', deletionQueueId)
          .single();

        if (!deletionQueue) {
          console.error('Deletion queue not found for ID:', deletionQueueId);
        } else {
          // Check if IT_DECONFIG task exists
          const { data: itDeconfigTask } = await supabase
            .from('tasks')
            .select('id, status')
            .eq('location_id', task.location_id)
            .eq('type', 'IT_DECONFIG')
            .maybeSingle();

          const shouldDeleteLocation = 
            (deletionQueue.deletion_stage === 'AWAITING_DATA_DECONFIG' && !itDeconfigTask) ||
            (deletionQueue.deletion_stage === 'AWAITING_BOTH_DECONFIG' && itDeconfigTask?.status === 'COMPLETED');

          if (shouldDeleteLocation) {
            // Delete the location
            const { error: deleteLocationError } = await supabase
              .from('locations')
              .delete()
              .eq('id', task.location_id);

            if (deleteLocationError) {
              console.error('Failed to delete location:', deleteLocationError);
              throw new Error(`Failed to delete location: ${deleteLocationError.message}`);
            }

            // Update deletion queue to DELETED
            const { error: updateQueueError } = await supabase
              .from('location_onboarding_deletion_queue')
              .update({
                deletion_stage: 'DELETED',
                deleted_at: new Date().toISOString(),
              })
              .eq('id', deletionQueueId);

            if (updateQueueError) {
              console.error('Failed to update deletion queue:', updateQueueError);
            }
          }
        }
      } else {
        // This is a regular deactivation task
        // Update deactivation queue status to COMPLETED
        // Deactivation flow is complete after data deconfig
        if (task.metadata?.deactivation_request_id) {
          await supabase
            .from('location_deactivation_queue')
            .update({
              status: 'DEACTIVATED',
              deactivated_at: new Date().toISOString(),
            })
            .eq('id', task.metadata.deactivation_request_id);
        } else if (task.location_id) {
          await supabase
            .from('location_deactivation_queue')
            .update({
              status: 'DEACTIVATED',
              deactivated_at: new Date().toISOString(),
            })
            .eq('location_id', task.location_id);
        }
      }

      // Log activity
      try {
        await supabase.rpc('log_activity', {
          p_user_id: user.id,
          p_action: 'TASK_COMPLETED' as any,
          p_description: `Data deconfiguration completed for ${task.location?.name}. Deactivation process complete.`,
          p_organization_id: task.organization_id,
          p_task_id: task.id,
          p_location_id: task.location_id || undefined,
        });
      } catch (logError) {
        console.error('Failed to log activity:', logError);
      }

      navigate('/data-tasks');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete task');
      setCompleting(false);
    }
  };

  // Render states
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading task...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">Error loading task</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  const isCompleted = task.status === 'COMPLETED';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <button
        onClick={() => navigate('/data-tasks')}
        className="flex items-center gap-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to Data Tasks</span>
      </button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          {/* Location Logo */}
          {task.location?.logo_url ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-20 h-20 rounded-full overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800">
                  <img
                    src={task.location.logo_url}
                    alt={`${task.location?.name} logo`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center shrink-0">
                  <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          )}

          {/* Header Content */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-red-600 dark:text-red-400">
                DEACTIVATION
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{task.location?.name}</span>
                </TooltipTrigger>
                <TooltipContent side="top">Location</TooltipContent>
              </Tooltip>
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{task.organization?.name}</span>
                </TooltipTrigger>
                <TooltipContent side="top">Organization</TooltipContent>
              </Tooltip>
            </p>
            <div className="mt-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <StatusBadge status={task.status} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Status</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Contact Information */}
      {contactInfo && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Primary Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {contactInfo.primary_contact_name && (
                <div>
                  <p className="text-xs text-muted-foreground">Contact Name</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_name}</p>
                </div>
              )}
              {contactInfo.primary_contact_email && (
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_email}</p>
                </div>
              )}
              {contactInfo.phone_number && (
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-medium text-sm">{contactInfo.phone_number}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Location Details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Data Pipeline Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {task.location?.description && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Description</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{task.location.description}</p>
            </div>
          )}

          {/* Current Data Configuration */}
          <div className="pt-4 border-t space-y-3">
            <h4 className="text-sm font-semibold">Current Data Configuration</h4>
            <div className="grid grid-cols-2 gap-4">
              {(task.location as any)?.source_schema && (
                <div>
                  <p className="text-xs text-muted-foreground">Source Schema</p>
                  <p className="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded">
                    {(task.location as any).source_schema}
                  </p>
                </div>
              )}
              {(task.location as any)?.open_dental_source_schema && (
                <div>
                  <p className="text-xs text-muted-foreground">OpenDental Schema</p>
                  <p className="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded">
                    {(task.location as any).open_dental_source_schema}
                  </p>
                </div>
              )}
            </div>
            
            <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 p-3 rounded-md">
              <p className="text-xs text-yellow-800 dark:text-yellow-200">
                <strong>Important:</strong> Ensure all data is archived before removing pipelines. This action will stop all data ingestion for this location.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Deconfiguration Checklist */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Data Deconfiguration Checklist</CardTitle>
          <CardDescription>
            {isCompleted
              ? 'Data deconfiguration completed. All steps were verified.'
              : 'Complete all steps before marking this task as done. This will complete the deactivation process.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="stoppedPipelines"
                checked={checklist.stoppedPipelines}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, stoppedPipelines: !!checked })
                }
                disabled={isCompleted}
              />
              <label
                htmlFor="stoppedPipelines"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Stopped all data ingestion pipelines
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="archivedData"
                checked={checklist.archivedData}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, archivedData: !!checked })
                }
                disabled={isCompleted}
              />
              <label
                htmlFor="archivedData"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Archived all historical data to cold storage
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="cleanedBigQuery"
                checked={checklist.cleanedBigQuery}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, cleanedBigQuery: !!checked })
                }
                disabled={isCompleted}
              />
              <label
                htmlFor="cleanedBigQuery"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Cleaned up BigQuery datasets and views
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="documentedRemoval"
                checked={checklist.documentedRemoval}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, documentedRemoval: !!checked })
                }
                disabled={isCompleted}
              />
              <label
                htmlFor="documentedRemoval"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Documented all data removal and archival procedures
              </label>
            </div>
          </div>

          {error && (
            <div className="mt-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 p-3 rounded-md">
              {error}
            </div>
          )}

          {!isCompleted && (
            <div className="mt-6">
              <div className="bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 p-4 rounded-md mb-4">
                <p className="text-sm text-orange-800 dark:text-orange-200 font-medium mb-2">
                  ⚠️ Final Step Warning
                </p>
                <p className="text-xs text-orange-700 dark:text-orange-300">
                  Completing this task will finalize the deactivation process. 
                  Ensure all data archival is complete before proceeding.
                </p>
              </div>
              
              <Button
                onClick={handleComplete}
                disabled={!allChecklistComplete || completing}
                className="w-full"
                size="lg"
                variant="destructive"
              >
                {completing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Completing Deactivation...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Complete Data Deconfiguration
                  </>
                )}
              </Button>
              {!allChecklistComplete && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Complete all checklist items to proceed
                </p>
              )}
            </div>
          )}

          {isCompleted && (
            <div className="mt-6 flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="font-semibold">Data Deconfiguration Complete</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default DataDeconfigTaskDetailPage;
